<script>
import { mapActions } from 'vuex';
import Comment from './comment.vue';
import AssignedUsers from './assigned-users.vue';

export default {
    props: {
        metric: {
            required: true,
            type: Object,
        },

        customStyle: {
            default: () => {
                return {
                    'main': {},
                    'title': {},
                    'textarea': {},
                    'date-update-data': {},
                };
            },
            type: Object,
        },

        showX: {
            default: true,
            type: Boolean,
        },
    },

    components: {
        Comment,
        AssignedUsers,
    },

    data() {
        return {
            loading: false,
            discussion: [],
            editors: [],
            auditors: [],

            newComment: {
                text: null,
                isActive: false,
            },

            updatingComment: null,
            deletingComment: null,
            replyingComment: null,

            requestInterval: null,
            loadingTimeout: null,
        }
    },

    async mounted() {
        await this._fetchDiscussion();
        
        this.requestInterval = setInterval(async () => {
            await this._fetchDiscussion();
        }, 10000);
        
        this.initialRouteQueryCheck();
    },

    beforeDestroy() {
        clearInterval(this.requestInterval);
        clearTimeout(this.loadingTimeout);
    },

    methods: {
        initialRouteQueryCheck() {
            const query = this.$route.query;
            if (query['comment'] != null && query['comment'] != undefined) {
                const el = window.document.getElementById(`comment_id_${query.comment}`);
                if (el) {
                    el.classList.add('flash');

                    el.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                }
            }
        },

        ...mapActions('KPIsMetrics', [
            'addComment',
            'updateComment',
            'deleteComment',
            'fetchDiscussion',
        ]),

        async _fetchDiscussion() {
            const params = {
                type: 'metric',
                relevant_id: this.metric.sust_metric_kpi_id
            };

            if (this.discussion.length === 0)
                await this.setLoading(true);
            await this.fetchDiscussion(params)
                .then(async (res) => {
                    await this.setDiscussion(res.data.value.discussion);
                    await this.setEditors(res.data.value.editors);
                    await this.setAuditors(res.data.value.auditors);
                    
                    this.loadingTimeout = setTimeout(async () => {
                        await this.setLoading(false);
                    }, 1000);
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messagenam)
                })
        },

        async _addComment() {
            if (this.newComment.text === null || this.newComment.text.length === 0) return;

            const params = {
                type: 'metric',
                body: {
                    relevant_id: this.metric.sust_metric_kpi_id,
                    text: this.newComment.text,
                    replying_to: this.replyingComment ? this.replyingComment.id : null,
                }
            };

            await this.addComment(params)
                .then(async (res) => {
                    await this.$notify('success', res.data.messageName);

                    const new_comment = {
                        id: res.data.id,
                        sender: `${localStorage.getItem('firstname')} ${localStorage.getItem('surname')}`,
                        text: params.body.text,
                        created_on: new Date(),
                        replying_to: params.body.replying_to,
                        replies: [],
                    };

                    if (new_comment.replying_to === null) {
                        this.discussion.unshift(new_comment);
                    } else {
                        this.replyingComment.replies.unshift(new_comment);
                    }

                    this.newComment.text = null;
                    this.newComment.isActive = false;
                    this.replyingComment = null;
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })
        },

        async _updateComment() {
            if (!this.updatingComment || (!this.updatingComment.text || this.updatingComment.text.length === 0) || (this.$user.role_id > 4 && this.updatingComment.created_by != this.$user.user_id)) return;

            const params = {
                id: this.updatingComment.id,
                body: {
                    text: this.updatingComment.text,
                }
            };

            await this.updateComment(params)
                .then(async (res) => {
                    await this.$notify('success', res.data.messageName);
                    const idx = this.discussion.findIndex(x => x.id === params.id);
                    if (idx != -1) {
                        this.discussion[idx].text = params.body.text;
                        this.discussion[idx].editor = `${localStorage.getItem('firstname')} ${localStorage.getItem('surname')}`;
                        this.discussion[idx].updated_on = new Date();
                    }

                    this.updatingComment = null;
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })
        },

        async _deleteComment() {
            if (!this.deletingComment || (this.$user.role_id > 4 && this.deletingComment.created_by != this.$user.user_id)) return;

            const params = {
                id: this.deletingComment.id
            };

            await this.deleteComment(params)
                .then(async (res) => {
                    await this.$notify('success', res.data.messageName);
                    await this.setDiscussion(this.discussion.filter(x => x.id != params.id));
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })
        },

        async handleTextareaKeydown(e, action) {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                await e.preventDefault();

                if (action === 'create') {
                    await this._addComment();
                } else {
                    await this._updateComment();
                }
            }
        },

        async setLoading(bool) {
            this.loading = bool;
        },

        async setDiscussion(_d) {
            this.discussion = _d;
        },

        async setEditors(_d) {
            this.editors = _d;
        },

        async setAuditors(_d) {
            this.auditors = _d;
        },

        setCommentInteraction(comment, action) {
            if (action === 'delete') {
                this.deletingComment = structuredClone(comment);
            } else if (action === 'update') {
                this.updatingComment = structuredClone(comment);
            } else if (action === 'reply') {
                this.replyingComment = comment;
            }
        },

        handleBottomButtonClick(key, comment) {
            if (key === 'share') {
                this.copyCommentLink(comment);
            } else if (key === 'reply') {
                this.setCommentInteraction(comment, 'reply');
            } else if (key === 'update') {
                this.setCommentInteraction(comment, 'update');
            } else if (key === 'delete') {
                this.setCommentInteraction(comment, 'delete');
            }
        },

        getQuotedMessage(quote_id) {
            const msg = this.discussion.find(x => x.id === quote_id);
            if (!msg) return;

            return {
                msg: msg,
                text: `${msg.sender} - ${msg.text.length > 20 ? msg.text.substring(0, 20).concat('...') : msg.text}`
            }
        },

        copyDiscussionLink() {
            navigator.clipboard.writeText(window.location.href);
            this.$notify('info', this.$t('metric_discussions.copied'), true);
        },

        copyCommentLink(_comment) {
            const query = { ...this.$route.query, comment: _comment.id };
            this.$router.replace({ query });
            navigator.clipboard.writeText(window.location.origin + this.$router.resolve({ query }).href);
            this.$notify('info', this.$t('metric_discussions.copied'), true);
        },

        handleCommentAction(e) {
            if (e === 'create') {
                this._addComment();
            } else if (e === 'update') {
                this._updateComment();
            } else if (e === 'delete') {
                this._deleteComment();
            }
        },
        
        handleCommentCancel(e) {
            if (e === 'reply') {
                this.replyingComment = null;
            } else if (e === 'update') {
                this.updatingComment = null;
            } else if (e === 'delete') {
                this.deletingComment = null;
            }
        },
    }
}
</script>

<template>
    <div style="width: 100%;">
        <div v-if="!loading"
            class="discussion" :style="customStyle['main']"
        >
            <div class="title" :style="customStyle['title']">
                <div>
                    {{ $t('metric_discussions.title').replace('#R#', metric ? metric.sust_metric_kpi_name : '') }}
                </div>
    
                <div>
                    <button class="icon-button" @click="copyDiscussionLink()">
                        <i class="fa-solid fa-share-nodes"
                            :style="{
                                'color': customStyle['title']['color'] || 'whitesmoke'
                            }"
                        ></i>
                    </button>
    
                    <button v-if="showX"
                        class="icon-button" @click="$emit('close')"
                    >
                        <i class="fa-solid fa-xmark"></i>
                    </button>
                </div>
            </div>
    
            <div class="flex-all-centered-row" style="justify-content: space-between;">
                <assigned-users 
                    :users="editors" 
                    :title="$t('metric_discussions.editors')" 
                    style="flex: 1"
                />
                <assigned-users 
                    :users="auditors" 
                    :title="$t('metric_discussions.auditors')" 
                    style="flex: 1"
                />
            </div>
    
            <div class="add-comment">
                <textarea v-if="newComment.isActive" 
                    :style="customStyle['textarea']"
                    v-model="newComment.text" name="newComment" id="newComment" rows="3"
                    @keydown="handleTextareaKeydown($event, 'create')"
                ></textarea>
                <textarea v-else 
                    :style="customStyle['textarea']"
                    name="newComment" id="newComment" rows="2"
                    :placeholder="$t('metric_discussions.add_comment_placeholder')"
                    @focus="() => newComment.isActive = true"
                ></textarea>
    
                <div v-if="newComment.isActive" class="buttons">
                    <button class="sust-btn sust-btn-green" @click="_addComment()">
                        {{ $t('metric_discussions.buttons.save') }}
                    </button>
    
                    <button class="icon-button" @click="() => newComment.isActive = false"
                        :style="{
                            'color': customStyle['textarea']['color'] || null
                        }"    
                    >
                        {{ $t('metric_discussions.buttons.cancel') }}
                    </button>
                </div>
            </div>
    
            <comment v-for="message in discussion" :key="message.id" 
                :id="`comment_id_${message.id}`"
                :message="message"
                :newComment="newComment"
                :updatingComment="updatingComment"
                :replyingComment="replyingComment"
                :deletingComment="deletingComment"
                :depth="0"
                :customStyle="customStyle"
                @action="handleCommentAction($event)"
                @cancel="handleCommentCancel($event)"
                @reply="handleBottomButtonClick('reply', $event)"
                @update="handleBottomButtonClick('update', $event)"
                @delete="handleBottomButtonClick('delete', $event)"
            />
        </div>

        <div v-else class="flex-all-centered-row" style="padding: 1em;" :style="customStyle['main']">
            <loading-spinner :color="customStyle['main']['color'] || 'whitesmoke'" fontSize="2em" />
        </div>
    </div>
</template>

<style lang='scss' scoped>
.discussion {
    display: flex;
    flex-direction: column;
    padding: 0 1em 1em 1em;
    background-color: var(--carbon);
    border-radius: .3em;
    color: white;
    overflow-x: hidden;

    > .title {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        position: sticky;
        top: 0;
        background-color: var(--carbon);
        z-index: 9999;
        margin-left: -1em;
        margin-right: -1em;
        padding: 1em;
    }
}

.add-comment {
    width: 100%;
    margin: 2em 0 .5em 0;
    min-height: 4em;

    > textarea {
        padding: .5em .75em;
        background-color: transparent;
        border: 0 !important;
        outline: thin solid lightgray;
        color: white;
        width: 100%;
        border-radius: .25em;
    }

    > .buttons {
        display: flex;
        max-height: 2em;

        > button {
            margin-right: .5em;
            box-shadow: none !important;
        }
    }

}

.icon-button {
    padding: 0 !important;
    margin-right: .5em;
    color: whitesmoke;

    &:hover {
        color: lightgray;
    }
}
</style>